import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/DropdownMenu';
import { ZustandPill } from './ZustandPill';
import { zustandAnzeige, zustaendeFuerBestellung } from '@/lib/zustandLabel';
import { useZustaende } from '@/hooks/useZustaende';
import { useZustandSetzen } from '@/hooks/useZustandSetzen';
import type { BestellungZeile } from '@/types/dashboard';
import { FARBE_ZUSTAND_FALLBACK } from '@/theme';
import { FOKUS_RING_INNEN } from '@/lib/fokus';

// A12.7 — Die Zustands-Pille im Brett wird bedienbar.
//
// „Kundin war da, Buch ist raus" ist der haeufigste Vorgang am Tresen,
// und er kostete bisher drei Schritte: Bestellung oeffnen, Zustand
// waehlen, zurueck. Von zehn Abholungen an einem Samstag macht das
// dreissig Schritte fuer zehn Aenderungen.
//
// DIESELBE LOGIK WIE IN DER BESTELLUNG
//   Wortlaut ueber `zustandAnzeige` (also „Abholbereit" statt „Bereit"
//   bei einer Abholung, E19), AUSWAHL ueber `zustaendeFuerBestellung`
//   (A46: bei Versand fehlt „Bereit"), Liste ueber `useZustaende` —
//   derselbe Query-Schluessel, also kein zweiter Abruf. Geschrieben
//   wird ueber denselben Endpunkt.
//
// BLEIBT DIE ZEILE ANKLICKBAR? JA.
//   Die Frage stellt sich, weil in der Zeile jetzt zwei Ziele liegen.
//   Sie bleibt es trotzdem: der Weg in die Bestellung ist die
//   haeufigste Handlung ueberhaupt, und wer ihn nur noch ueber die
//   Bestellnummer faende, muesste auf einem Tablet ein 112 px breites
//   Ziel treffen statt der ganzen Zeile. Die Pille faengt ihren Tipp
//   selbst ab (`stopPropagation` am Ausloeser), und sie ist als
//   Bedienelement erkennbar — sie traegt ein Winkelzeichen, die uebrige
//   Zeile nicht.
//
// KEIN SENDUNGSDIALOG VON HIER AUS — Begruendung in useZustandSetzen.

type Props = { zeile: BestellungZeile };

export function ZustandMenu({ zeile: b }: Props) {
  const q = useZustaende();
  const setzen = useZustandSetzen();
  const versandartCode = b.versandart?.code ?? null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Zustand von ${b.bestellnr} ändern, derzeit ${zustandAnzeige(
            b.zustand.code,
            b.zustand.label,
            versandartCode
          )}`}
          // `touch-ziel` statt echter 44 px: die Zeile ist gut 26 px hoch
          // im Inhalt, ein 44 px hoher Ausloeser haette JEDE Zeile
          // aufgeblaeht — und A10 hat sie gerade erst von 78-108 auf
          // 42-51 px gebracht. Die unsichtbare Flaeche liegt trotzdem da.
          //
          // EIN FOKUSRING STATT ZWEIER (A42.3, Nebenbefund aus A39).
          // Bis hierher zeigte dieser Auslöser seinen Fokus über den
          // BROWSER-EIGENEN Ring (`outline: auto`), während der
          // Zeilen-Link seit A39 den Akzentring trägt — zwei
          // verschiedene Fokusbilder direkt nebeneinander in derselben
          // Zeile. Jetzt ist es eines.
          //
          // `ring-inset` wie am Zeilen-Link, und aus demselben Grund:
          // Die Tabellenzelle trägt `overflow-hidden`, damit `truncate`
          // greift. Ein außen gezeichneter Ring wäre dort abgeschnitten
          // — im Browser an Bildpunkten nachgemessen, nicht aus der
          // Klassenliste geschlossen.
          className={`touch-ziel inline-flex shrink-0 rounded-full ${FOKUS_RING_INNEN}`}
          onClick={(e) => e.stopPropagation()}
        >
          <ZustandPill
            code={b.zustand.code}
            label={b.zustand.label}
            color={b.zustand.color}
            versandartCode={versandartCode}
            chevron
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
        {/* Dieselbe Auswahl wie in der Bestellung, aus derselben
            Funktion (A46). Zwei Menüs mit je eigener Liste liefen
            auseinander — dann böte das Brett an, was die Bestellung
            verweigert. */}
        {zustaendeFuerBestellung(q.data?.items, versandartCode).map((z) => (
          <DropdownMenuItem
            key={z.id}
            onSelect={() => {
              if (z.code === b.zustand.code) return;
              setzen.mutate({ id: b.id, bestellnr: b.bestellnr, zustand_code: z.code });
            }}
          >
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: z.farbe ?? FARBE_ZUSTAND_FALLBACK }}
            />
            {zustandAnzeige(z.code, z.label, versandartCode)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
